<template>
    <div class="pd-hero" v-if="pd && settled">
        <section-header title="Professional Development" icon="fa-arrow-trend-up" :to="sectionRoute" link-text="Open Professional Development"/>
        <ui-card>
            <h2 class="hero-title">{{ headline.title }}</h2>
            <p class="hero-text">{{ headline.text }}</p>
            <!-- The cost of waiting, in red: the one number that pushes -->
            <div class="mt-3" v-if="firstTarget && firstTargetFiveYear > 0">
                <span class="loss-badge"><i class="fas fa-hourglass-half mr-2"></i>Waiting costs you {{ firstTargetFiveYear | money }} over the next five years</span>
            </div>

            <div class="hero-stats" v-if="stats.length">
                <div class="hero-stat" v-for="s in stats" :key="s.label">
                    <span class="stat-value" :class="s.cls">{{ s.value }}</span>
                    <span class="stat-label">{{ s.label }}</span>
                </div>
            </div>

            <template #footer>
                <div class="hero-actions">
                    <div class="hero-note" v-if="salary.ready">
                        Based on the district, degree, column and years you've told us. <router-link :to="settingsRoute">Update your information</router-link> if anything has changed.
                    </div>
                    <div class="box-buttons">
                        <b-button type="is-primary" size="is-small" tag="router-link" :to="headline.to"><i class="fas mr-2" :class="headline.icon"></i>{{ headline.cta }}</b-button>
                    </div>
                </div>
            </template>
        </ui-card>
    </div>
</template>

<script>
    import UiCard from "../UiCard";
    import SectionHeader from "../framework/SectionHeader";
    import salaryLane from "../pd/salaryLane";
    import {formatHours, pdRoute} from "@/utils/pd";

    // Whole-dollar, comma grouped
    function money(v) {
        return '$' + Math.round(parseFloat(v) || 0).toLocaleString('en-US');
    }

    /**
     * The dashboard's professional-development card: ONE headline (the
     * strongest thing we can say — the next salary column and what it's
     * worth, else renewal progress, else the ask for career details), a
     * row of numbers, and the way into the Professional Development
     * section. The section itself carries the detail; the dashboard just
     * points there.
     */
    export default {
        name: "PdHero",
        components: {UiCard, SectionHeader},
        mixins: [salaryLane],
        computed: {
            pd() {
                return this.dashboardPd;
            },
            sectionRoute() {
                return pdRoute();
            },
            settingsRoute() {
                return pdRoute('settings');
            },
            // Don't paint a generic headline while the schedule is still on
            // its way — the specific one lands a moment later
            settled() {
                return !this.scaleLoading;
            },
            headline() {
                const pd = this.pd;
                const s = this.salary;
                const t = this.firstTarget;
                if (t) {
                    const five = this.firstTargetFiveYear;
                    return {
                        title: "You're " + t.requiredHours + ' clock hours from ' + t.tier + '.',
                        text: "That's about +" + money(t.diff) + ' a year on ' + s.district_name + "'s salary schedule" +
                            (five > 0 ? ' — ' + money(five) + ' more over the next five years.' : '.'),
                        cta: "See What It's Worth", icon: 'fa-chart-line-up', to: pdRoute('salary'),
                    };
                }
                if (pd.profile.declared) {
                    const counted = (parseFloat(pd.hours.counted) || 0) + (parseFloat(pd.hours.external) || 0);
                    const remaining = parseFloat(pd.hours.remaining_to_target) || 0;
                    return {
                        title: formatHours(counted) + ' of ' + pd.profile.target_hours + ' hours toward your renewal.',
                        text: remaining > 0
                            ? formatHours(remaining) + ' more by ' + this.$options.filters.dateOnly(pd.profile.renewal_on) +
                              (pd.hours.in_progress.potential_hours ? ' — ' + formatHours(pd.hours.in_progress.potential_hours) + ' are already in progress.' : '. Clock hours at bundle pricing are the fastest way there.')
                            : "You've reached your renewal target. Hours you keep earning still count toward your next salary column.",
                        cta: 'Track Your Renewal', icon: 'fa-calendar-check', to: pdRoute('renewal'),
                    };
                }
                if (s.ready && this.position && !t) {
                    return {
                        title: "You're at the top of " + s.district_name + "'s " + s.highest_degree + ' track.',
                        text: 'Every clock hour still counts toward your certificate renewal — set a renewal date and we\'ll track your progress.',
                        cta: 'Track Your Renewal', icon: 'fa-calendar-check', to: pdRoute('renewal'),
                    };
                }
                if (s.ready) {
                    return {
                        title: 'See where you stand.',
                        text: "We don't have " + (s.district_name || 'your district') + "'s salary schedule on file yet. Your clock hours, renewal tracking and history are all in one place.",
                        cta: 'Open Professional Development', icon: 'fa-arrow-trend-up', to: pdRoute(),
                    };
                }
                return {
                    title: 'See what your next salary column is worth.',
                    text: "Add your district, degree and current clock-hour column and we'll show how close you are to the next column on your salary schedule — and what moving up pays.",
                    cta: 'Add Career Details', icon: 'fa-plus', to: pdRoute('salary'),
                };
            },
            stats() {
                const pd = this.pd;
                const out = [];
                if (parseFloat(pd.hours.earned_total) > 0) {
                    out.push({value: formatHours(pd.hours.earned_total), label: 'clock hours earned'});
                }
                if (pd.hours.in_progress.potential_hours) {
                    out.push({value: formatHours(pd.hours.in_progress.potential_hours), label: 'in progress'});
                }
                if (pd.profile.declared) {
                    const counted = (parseFloat(pd.hours.counted) || 0) + (parseFloat(pd.hours.external) || 0);
                    out.push({value: formatHours(counted) + ' / ' + pd.profile.target_hours, label: 'toward renewal'});
                }
                if (this.firstTarget) {
                    out.push({value: '+' + money(this.firstTarget.diff), label: 'a year at ' + this.firstTarget.tier, cls: 'is-gain'});
                }
                return out;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .hero-title {
        font-size: 1.6rem;
        font-weight: 800;
        line-height: 1.2;
        color: #213f6a;

        @media (max-width: 600px) {
            font-size: 1.3rem;
        }
    }

    .hero-text {
        margin-top: 0.5rem;
        color: #4a5568;
    }

    // Red counterpart of the green "save 33%" pill
    .loss-badge {
        display: inline-block;
        background: #fbe9e7;
        color: #a83e33;
        font-size: 0.7rem;
        font-weight: 900;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 3px 10px;
        border-radius: 999px;
    }

    .hero-stats {
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem 2rem;
        margin-top: 1.1rem;

        .hero-stat {
            display: flex;
            align-items: baseline;
            gap: 0.4rem;
        }

        .stat-value {
            font-size: 1.35rem;
            font-weight: 800;
            color: #336699;

            &.is-gain {
                color: #2e7d4f;
            }
        }

        .stat-label {
            font-size: 0.8rem;
            color: #7a7a7a;
        }
    }

    .hero-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;

        .hero-note {
            flex: 1 1 260px;
            font-size: 0.75rem;
            color: #7a8794;
        }

        .box-buttons {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
            margin-left: auto;
        }
    }

</style>
